import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Cart-drawer Subscribe & Save per-line mutator (feat/lg-cart-subscribe-save).
 *
 * Document-level delegation, NOT a custom element wrapping the controls: the
 * toggle / segmented buttons live INSIDE the drawer's keyed morph target
 * (`div[data-hydration-key="cart-drawer-inner"]`, §4.5), so they are re-created
 * on every mutation. Delegating from `document` (this module loads OUTSIDE the
 * morph target, next to lg-cart-samples.js — a <script> inside the fragment is
 * inert, §4.5 / BLOCKER 1) means we never re-bind and never hold a stale
 * element reference across a morph.
 *
 * Mutation contract (horizon-mechanics §4.14 + §4.15):
 * - POST /cart/change.js with `{ line, quantity, selling_plan, sections,
 *   sections_url }`. Line addressing is INDEX-only (§4.14) — the 1-based line
 *   index rides on `data-line`. `quantity` is read LIVE from the row's stepper
 *   input at click time (debounce race — never a Liquid attr). Toggle OFF sends
 *   `selling_plan: ''`.
 * - Dispatch `CartLinesUpdateEvent` with `action: 'update'` (NEVER 'add' — the
 *   §4.7 auto-open gate stays cold) and resolve it WITH `detail.sections`
 *   mirroring component-cart-items.js:271-280. We do NOT morph ourselves —
 *   cart-items-component#handleCartUpdate owns the single morph (§4.15; resolving
 *   without detail.sections triggers its cache:false fallback refetch = double
 *   render).
 * - Focus restore (§4.14): the plan switch changes the line key → the row is
 *   id-key REPLACED by the morph, focus drops to <body>. After the morph
 *   settles we re-locate the control by line index + kind and refocus it.
 */

const SELECTOR = '[data-lg-sub-toggle], [data-lg-sub-freq]';
const DISABLED_CLASS = 'cart-items-disabled';

// Monotonic flight counter — each mutate() bumps it, and any pending error
// re-surfacer (see surfaceErrorAfterMorph) from an OLDER flight goes inert, so
// a stale failure message can never repaint over a newer mutation's render.
let flightGeneration = 0;

/**
 * Reads the live quantity for a line from its cart stepper input, so a pending
 * (debounced) qty edit is not clobbered by the plan switch. Falls back to 1.
 * @param {Element} control - the toggle/seg control that was activated.
 * @returns {number}
 */
function liveQuantity(control) {
  const row = control.closest('.cart-line');
  const input = row?.querySelector('input[data-cart-quantity], .quantity-selector input[type="number"], input[type="number"]');
  const value = input instanceof HTMLInputElement ? parseInt(input.value, 10) : NaN;
  return Number.isFinite(value) && value > 0 ? value : 1;
}

/**
 * Collects the SRA section ids to re-render (same walk as
 * component-cart-items.js updateQuantity).
 * @returns {string}
 */
function sectionsParam() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids).join(',');
}

/**
 * @param {Element} control
 * @param {string} kind - 'toggle' | 'freq'
 */
async function mutate(control, kind) {
  const line = control.getAttribute('data-line');
  if (!line) return;
  flightGeneration += 1;

  // Resolve the plan id to apply. Toggle: '' when unchecking, the default plan id
  // when checking. Frequency: the button/option's own plan id.
  let sellingPlan = '';
  if (kind === 'toggle') {
    const input = /** @type {HTMLInputElement} */ (control);
    sellingPlan = input.checked ? input.getAttribute('data-plan-id') || '' : '';
  } else {
    if (control instanceof HTMLSelectElement) {
      sellingPlan = control.value;
    } else {
      sellingPlan = control.getAttribute('data-plan-id') || '';
    }
  }

  const quantity = liveQuantity(control);

  // The row's line-item key — carried on the dispatched event's `lines` payload
  // (matching the vendor dispatch, component-cart-items.js:234-241) so listeners
  // can identify the mutated line. The REQUEST addressing stays index-based
  // (§4.14: change.js is line-index only).
  const row = control.closest('.cart-line');
  const lineKey = row instanceof HTMLElement ? row.dataset.key ?? '' : '';

  // Guard against a concurrent stepper/toggle race — mirror the vendor
  // `cart-items-disabled` pattern so controls can't be double-fired mid-request.
  // The visible in-flight layer (dim + pointer blocking on line controls, the
  // subscribe controls, and the checkout CTA — all inside the host subtree) is
  // pure CSS keyed on this class in lg-cart.css (shared by drawer + /cart page
  // since feat/cart-page-drawer-parity), so it self-heals across
  // the morph. aria-busy marks the flight for AT; the fired control also gets
  // the native `disabled` attr (the .switch primitive ships its 0.45 disabled
  // dim for free) — restored in `finally` as belt-and-braces for the error
  // path (on success the morph replaces the row anyway).
  const host = control.closest('cart-items-component');
  // Re-entry guard (review round 4): pointer events are CSS-blocked during a
  // flight, but a KEYBOARD-initiated second fire (Space/Enter on a still-
  // focused control) bypasses pointer-events — no-op while any flight holds
  // the class (vendor qty flights included).
  if (host?.classList.contains(DISABLED_CLASS)) return;
  host?.classList.add(DISABLED_CLASS);
  host?.setAttribute('aria-busy', 'true');
  const canDisable =
    control instanceof HTMLInputElement ||
    control instanceof HTMLButtonElement ||
    control instanceof HTMLSelectElement;
  if (canDisable) control.disabled = true;

  const sections = sectionsParam();

  // action 'update' (never 'add') keeps the §4.7 auto-open gate cold.
  const deferred = CartLinesUpdateEvent.createPromise();
  control.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: [{ id: lineKey, quantity }],
      promise: deferred.promise,
    })
  );

  try {
    // §4.14 hardening (attempt 5): line-index addressing is POSITIONAL and can
    // go stale — any concurrent mutation that removes or merges lines shifts
    // the indexes between our server render and this click. The known
    // in-drawer producer is the samples auto-remove guard: subscribing can
    // drop the paid subtotal below the samples threshold (accepted Q2
    // consequence), the guard zeroes the sample lines, and every data-line
    // BELOW them is suddenly off by N — a stale index then mutates the WRONG
    // line or 422s ("Cart Error") when the mis-targeted variant doesn't carry
    // the plan. Re-resolve the index at click time by the row's line KEY
    // against live /cart.js truth (keys are stable within a render cycle;
    // index resolution is ours because change.js only accepts `line`).
    let resolvedLine = Number(line);
    if (lineKey) {
      const cartRes = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      const cartData = await cartRes.json();
      const idx = Array.isArray(cartData.items) ? cartData.items.findIndex((it) => it.key === lineKey) : -1;
      if (idx === -1) {
        // The line no longer exists server-side (removed/merged under us).
        // Skip the change entirely and resolve sections-absent so the vendor
        // cache:false refetch re-renders the drawer to current truth.
        console.warn('lg-cart-subscribe: line key vanished before mutation — refreshing drawer', lineKey);
        deferred.resolve({
          cart: null,
          detail: { itemCount: cartData.item_count ?? currentItemCount(), source: 'lg-cart-subscribe', didError: true },
        });
        return;
      }
      resolvedLine = idx + 1;
    }

    const body = JSON.stringify({
      line: resolvedLine,
      quantity,
      selling_plan: sellingPlan,
      sections,
      sections_url: window.location.pathname,
    });

    const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
    const data = await response.json();

    if (data.errors) {
      // change.js rejected the mutation (e.g. 422 "Cart Error"). Do NOT build
      // a cart from the error payload: it carries no currency/line data and
      // createCartFromAjaxResponse throws (Intl "Currency code is required
      // with currency style") INSIDE this try — which used to dead-end the
      // error path before the resolve ran, so the truth-restore refetch never
      // fired and the UI looked hung (G3 attempt-5 fix). Instead resolve
      // WITHOUT `detail.sections`: #handleCartUpdate then runs its
      // sections-absent fallback (sectionRenderer.renderSection cache:false,
      // component-cart-items.js:382-384) — the refetch that re-renders the
      // drawer from server state. detail.itemCount carries the CURRENT count
      // read from the DOM so the header bubble doesn't zero (cart-icon.js:60
      // falls back cart → detail.itemCount → 0). The raw server text goes to
      // the console only; the shopper sees the locale string.
      console.error('lg-cart-subscribe: cart change rejected', data.errors);
      // Immediate surface addresses the CURRENT render's error container, so it
      // uses the render-time `line` the refs were emitted with.
      surfaceError(line, subscribeErrorText());
      // Instant visual truth while the refetch is in flight; the re-render
      // then confirms it from server state.
      if (kind === 'toggle' && control instanceof HTMLInputElement) {
        control.checked = !control.checked;
      }
      deferred.resolve({
        cart: null,
        detail: { itemCount: currentItemCount(), source: 'lg-cart-subscribe', didError: true },
      });
      // The sections-absent resolve above triggers the vendor cache:false
      // truth-restore refetch, whose re-render replaces the error container
      // with a hidden/empty one — wiping the message the shopper just got.
      // Re-surface it once the refetch morph lands; the fresh render's refs
      // are indexed from live cart truth, so address by `resolvedLine`.
      surfaceErrorAfterMorph(String(resolvedLine), subscribeErrorText());
      return;
    }

    const newCount = readItemCount(data, sections);

    // Resolve WITH detail.sections so cart-items-component performs the single
    // morph (§4.15). We do NOT morph here.
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
      detail: {
        sections: data.sections,
        items: data.items,
        itemCount: newCount,
        source: 'lg-cart-subscribe',
        didError: false,
      },
    });

    // Focus restore (§4.14): the row is REPLACED (line key changed). Wait for the
    // morph to settle, then re-locate the control by line index + kind. Address
    // by `resolvedLine` (live-truth index), not the render-time `line` — a
    // samples-guard removal between render and click shifts every index below
    // it (§4.12), and the post-morph DOM is rendered from live truth.
    restoreFocusAfterMorph(String(resolvedLine), kind);
  } catch (error) {
    deferred.reject(error);
    // Network failure → no response sections, no fallback refetch: revert the
    // flipped toggle so the UI doesn't show a state the server never accepted.
    // (Seg buttons need no revert — their pressed state is class-driven from
    // the server render and was never optimistically changed.)
    if (kind === 'toggle' && control instanceof HTMLInputElement) {
      control.checked = !control.checked;
    }
    console.error('lg-cart-subscribe: failed to update selling plan', error);
  } finally {
    // Restore the fired control first (stale after a success morph replaced
    // the row — harmless; the error path is where this matters).
    if (canDisable) control.disabled = false;
    host?.classList.remove(DISABLED_CLASS);
    host?.removeAttribute('aria-busy');
  }
}

/**
 * Current cart item count read from the last server render (the machinery
 * contract's hidden `[ref="cartItemCount"]`) — used on the error path so the
 * header bubble keeps its count (cart-icon.js:60: cart → detail.itemCount → 0).
 * The subscribe controls only exist in a rendered non-empty cart, so the ref
 * is always present when this can run.
 * @returns {number | undefined}
 */
function currentItemCount() {
  const text = document.querySelector('[ref="cartItemCount"]')?.textContent;
  const count = text ? parseInt(text, 10) : NaN;
  return Number.isFinite(count) ? count : undefined;
}

/**
 * Shopper-facing error copy — the `lg.cart.subscribe_error` locale string,
 * emitted as a JSON block by lg-cart-drawer.liquid OUTSIDE the morph target
 * (same placement rationale as this module's script tag). English fallback
 * mirrors the vendor `Theme.translations?.x || '…'` pattern
 * (gift-card-recipient-form.js:147).
 * @returns {string}
 */
function subscribeErrorText() {
  const el = document.querySelector('script[data-lg-subscribe-i18n]');
  try {
    const parsed = el?.textContent ? JSON.parse(el.textContent) : null;
    if (parsed && typeof parsed.error === 'string' && parsed.error) return parsed.error;
  } catch {
    // fall through to the default
  }
  return 'Could not update the subscription. Please try again.';
}

/**
 * Reads the post-mutation item count from the re-rendered cart section (the
 * hidden `[ref="cartItemCount"]`), matching component-cart-items.js:265-266.
 * @param {any} data
 * @param {string} sections
 * @returns {number}
 */
function readItemCount(data, sections) {
  const firstSection = sections.split(',')[0];
  const html = data.sections?.[firstSection];
  if (!html) return 0;
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const text = parsed.querySelector('[ref="cartItemCount"]')?.textContent;
  return text ? parseInt(text, 10) : 0;
}

/**
 * Re-focuses the control for `line` after the drawer morph replaces the row.
 * The morph is driven by cart-items-component on a promise chain we can't await
 * directly here, so observe the drawer inner subtree for the replacement and
 * refocus the first matching control (preventScroll — the drawer shouldn't jump).
 * @param {string} line
 * @param {string} kind - 'toggle' | 'freq'
 */
function restoreFocusAfterMorph(line, kind) {
  const inner = document.querySelector('[data-hydration-key="cart-drawer-inner"]');
  if (!(inner instanceof HTMLElement)) return;

  const attr = kind === 'toggle' ? 'data-lg-sub-toggle' : 'data-lg-sub-freq';
  let settled = false;

  const focusNow = () => {
    // Only restore when focus is actually ORPHANED by the row replacement —
    // i.e. it fell back to <body> (or the previously-focused node is gone).
    // While focus still sits on a connected element this returns false: either
    // the morph hasn't replaced the row yet (keep observing), or the user
    // tabbed elsewhere during the wait window (never yank focus; the timeout's
    // final attempt no-ops through this same guard).
    const active = document.activeElement;
    if (active && active !== document.body && active.isConnected) return false;

    // For freq, prefer the currently-selected button so focus lands on the new
    // frequency; fall back to the first control for the line.
    const controls = inner.querySelectorAll(`[${attr}][data-line="${CSS.escape(line)}"]`);
    if (!controls.length) return false;
    let target = /** @type {HTMLElement} */ (controls[0]);
    if (kind === 'freq') {
      const selected = Array.from(controls).find(
        (c) => c.classList.contains('is-on') || c.getAttribute('aria-pressed') === 'true'
      );
      if (selected instanceof HTMLElement) target = selected;
    }
    target.focus({ preventScroll: true });
    return true;
  };

  const observer = new MutationObserver(() => {
    if (settled) return;
    if (focusNow()) {
      settled = true;
      observer.disconnect();
    }
  });
  observer.observe(inner, { childList: true, subtree: true });

  // Safety timeout — if no mutation lands (e.g. morph was a no-op), stop
  // observing and attempt a single restore against the current DOM.
  setTimeout(() => {
    if (settled) return;
    settled = true;
    observer.disconnect();
    focusNow();
  }, 1500);
}

/**
 * Keeps a change.js error visible ACROSS the truth-restore refetch. The
 * sections-absent resolve makes cart-items-component re-render the drawer
 * (cache:false, §4.15), which replaces the error container with a fresh hidden
 * one — so the message surfaced pre-resolve is wiped moments later. Observe the
 * drawer inner and re-apply the message on each morph batch inside a bounded
 * window (the refetch can be slow on a throttled network), disconnecting the
 * observer around our own write so it never self-triggers. A newer mutate()
 * flight (flightGeneration bump) or the window elapsing retires the observer —
 * a newer flight's render is fresher truth and must not be repainted over.
 * @param {string} line - live-truth 1-based line index (matches the refetched render's refs)
 * @param {string} message
 */
function surfaceErrorAfterMorph(line, message) {
  const inner = document.querySelector('[data-hydration-key="cart-drawer-inner"]');
  if (!(inner instanceof HTMLElement)) return;

  const generation = flightGeneration;
  let retired = false;
  const observer = new MutationObserver(() => {
    if (retired) return;
    if (flightGeneration !== generation) {
      retired = true;
      observer.disconnect();
      return;
    }
    observer.disconnect();
    surfaceError(line, message);
    if (!retired) observer.observe(inner, { childList: true, subtree: true });
  });
  observer.observe(inner, { childList: true, subtree: true });

  setTimeout(() => {
    retired = true;
    observer.disconnect();
  }, 4000);
}

/**
 * Surfaces a change.js error via the per-line error container if reachable.
 * @param {string} line
 * @param {string} message
 */
function surfaceError(line, message) {
  const container = document.querySelector(`[ref="cartItemErrorContainer-${CSS.escape(line)}"]`);
  const text = document.querySelector(`[ref="cartItemError-${CSS.escape(line)}"]`);
  if (container instanceof HTMLElement && text instanceof HTMLElement) {
    text.textContent = message;
    container.classList.remove('hidden');
  }
}

// --- Delegated listeners (document-level; the controls are morph-replaced). ---

document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  if (target.matches('input[data-lg-sub-toggle]')) {
    mutate(target, 'toggle');
  } else if (target.matches('select[data-lg-sub-freq]')) {
    mutate(target, 'freq');
  }
});

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const btn = target.closest('button[data-lg-sub-freq]');
  if (!btn) return;
  // Ignore clicks on the already-selected frequency (no-op mutation).
  if (btn.classList.contains('is-on')) return;
  mutate(btn, 'freq');
});
